import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatNumber, formatDateRange, calculatePercentage, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis,
    Tooltip, CartesianGrid, Legend, BarChart, Bar
} from 'recharts';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    TrendingUp, 
    TrendingDown, 
    Minus,
    Calendar,
    ArrowRight,
    RefreshCw,
    Linkedin,
    Users,
    BarChart3,
    LineChart as LineChartIcon
} from 'lucide-react';

const ComparisonIndicator = ({ current, previous, suffix = '' }) => {
    if (current === previous) {
        return <Minus className="w-4 h-4 text-content-muted" />;
    }
    const isUp = current > previous;
    const change = previous === 0 ? 100 : Math.round(((current - previous) / previous) * 100);
    
    return (
        <div className={`flex items-center gap-1 ${isUp ? 'text-status-success' : 'text-status-error'}`}>
            {isUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span className="text-sm font-bold">{isUp ? '+' : ''}{change}{suffix}</span>
        </div>
    );
};

const MetricComparisonRow = ({ label, current, previous, format = 'number' }) => {
    const formatValue = (val) => {
        if (format === 'percent') return `${val}%`;
        if (format === 'currency') return `$${formatNumber(val)}`;
        return formatNumber(val);
    };

    return (
        <div className="flex items-center justify-between py-3 border-b border-line-default last:border-0">
            <span className="text-content-secondary">{label}</span>
            <div className="flex items-center gap-6">
                <span className="text-white font-medium w-20 text-right">{formatValue(previous)}</span>
                <ArrowRight className="w-4 h-4 text-content-muted" />
                <span className="text-white font-medium w-20 text-right">{formatValue(current)}</span>
                <div className="w-24 text-right">
                    <ComparisonIndicator current={current} previous={previous} suffix="%" />
                </div>
            </div>
        </div>
    );
};

const ProfileComparisonCard = ({ profile }) => {
    const acceptRate = calculatePercentage(profile.connection_accepted, profile.reachouts_sent);
    const responseRate = calculatePercentage(profile.responses, profile.reachouts_sent);
    const mqlRate = calculatePercentage(profile.mqls, profile.reachouts_sent);

    return (
        <div className="card-base p-4 hover:border-channel-linkedin/50 transition-colors">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-channel-linkedin/20 flex items-center justify-center">
                    <Linkedin className="w-5 h-5 text-channel-linkedin" />
                </div>
                <div>
                    <h4 className="font-heading font-bold text-white">{profile.profile_name}</h4>
                    <span className="text-xs text-content-muted">{profile.report_count || 0} reports</span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-surface-app p-2 rounded text-center">
                    <p className="text-lg font-bold text-white">{formatNumber(profile.reachouts_sent)}</p>
                    <p className="text-xs text-content-muted">Reachouts</p>
                </div>
                <div className="bg-surface-app p-2 rounded text-center">
                    <p className="text-lg font-bold text-channel-linkedin">{formatNumber(profile.connection_accepted)}</p>
                    <p className="text-xs text-content-muted">Accepted</p>
                </div>
                <div className="bg-surface-app p-2 rounded text-center">
                    <p className="text-lg font-bold text-white">{formatNumber(profile.responses)}</p>
                    <p className="text-xs text-content-muted">Responses</p>
                </div>
                <div className="bg-surface-app p-2 rounded text-center">
                    <p className="text-lg font-bold text-status-success">{formatNumber(profile.positive_responses)}</p>
                    <p className="text-xs text-content-muted">Positive</p>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-brand-red/10 p-2 rounded text-center border border-brand-red/30">
                    <p className="text-xl font-bold text-brand-red">{profile.mqls}</p>
                    <p className="text-xs text-brand-red/70">MQLs</p>
                </div>
                <div className="bg-status-success/10 p-2 rounded text-center border border-status-success/30">
                    <p className="text-xl font-bold text-status-success">{profile.qualified_leads}</p>
                    <p className="text-xs text-status-success/70">SQLs</p>
                </div>
            </div>

            <div className="space-y-2 pt-3 border-t border-line-default">
                <div className="flex justify-between text-sm">
                    <span className="text-content-muted">Accept Rate</span>
                    <span className="text-channel-linkedin font-bold">{acceptRate}%</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-content-muted">Response Rate</span>
                    <span className="text-white font-bold">{responseRate}%</span>
                </div>
                <div className="flex justify-between text-sm">
                    <span className="text-content-muted">MQL Rate</span>
                    <span className="text-brand-red font-bold">{mqlRate}%</span>
                </div>
            </div>
        </div>
    );
};

export default function ComparisonPage() {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [comparisonType, setComparisonType] = useState('wow');
    const [period1Id, setPeriod1Id] = useState('');
    const [period2Id, setPeriod2Id] = useState('');
    const [comparisonData, setComparisonData] = useState(null);
    const [comparing, setComparing] = useState(false);

    // LinkedIn Profile Comparison State
    const [allProfiles, setAllProfiles] = useState([]);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    const [profileComparisonData, setProfileComparisonData] = useState([]);
    const [comparingProfiles, setComparingProfiles] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const response = await reportsApi.getAll();
            setReports(response.data);
            
            // Extract all unique profiles
            const profileMap = {};
            response.data.forEach(report => {
                const profiles = report.linkedin?.profiles || [];
                profiles.forEach(profile => {
                    const name = profile.profile_name;
                    if (!name) return;
                    if (!profileMap[name]) {
                        profileMap[name] = {
                            profile_name: name,
                            reachouts_sent: 0,
                            connection_accepted: 0,
                            responses: 0,
                            positive_responses: 0,
                            rejections: 0,
                            in_progress: 0,
                            mqls: 0,
                            qualified_leads: 0,
                            report_count: 0
                        };
                    }
                    profileMap[name].reachouts_sent += profile.reachouts_sent || 0;
                    profileMap[name].connection_accepted += profile.connection_accepted || 0;
                    profileMap[name].responses += profile.responses || 0;
                    profileMap[name].positive_responses += profile.positive_responses || 0;
                    profileMap[name].rejections += profile.rejections || 0;
                    profileMap[name].in_progress += profile.in_progress || 0;
                    profileMap[name].mqls += profile.mqls || 0;
                    profileMap[name].qualified_leads += profile.qualified_leads || 0;
                    profileMap[name].report_count += 1;
                });
            });
            setAllProfiles(Object.values(profileMap));

            if (response.data.length >= 2) {
                setPeriod1Id(response.data[0].id);
                setPeriod2Id(response.data[1].id);
            }
        } catch (error) {
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleCompare = async () => {
        if (!period1Id || !period2Id) {
            toast.error('Please select both periods to compare');
            return;
        }
        if (period1Id === period2Id) {
            toast.error('Please select different periods');
            return;
        }

        setComparing(true);
        try {
            const response = await reportsApi.compare({
                customer_id: 'all',
                comparison_type: comparisonType,
                period1_id: period1Id,
                period2_id: period2Id,
            });
            setComparisonData(response.data);
        } catch (error) {
            toast.error('Failed to compare reports');
        } finally {
            setComparing(false);
        }
    };

    const toggleProfileSelection = (profileName) => {
        setSelectedProfiles(prev => {
            if (prev.includes(profileName)) {
                return prev.filter(p => p !== profileName);
            }
            if (prev.length >= 5) {
                toast.error('Maximum 5 profiles can be compared');
                return prev;
            }
            return [...prev, profileName];
        });
    };

    const handleCompareProfiles = async () => {
        if (selectedProfiles.length < 2) {
            toast.error('Please select at least 2 profiles to compare');
            return;
        }

        setComparingProfiles(true);
        try {
            const response = await reportsApi.compareLinkedInProfiles({
                profile_names: selectedProfiles
            });
            setProfileComparisonData(response.data.profiles);
        } catch (error) {
            // If API fails, use local data
            const filteredProfiles = allProfiles.filter(p => selectedProfiles.includes(p.profile_name));
            setProfileComparisonData(filteredProfiles);
        } finally {
            setComparingProfiles(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="comparison-page">
            <Header 
                title="Comparison Views"
                subtitle="Week-on-Week, Month-on-Month, and LinkedIn Profile comparisons"
            />

            <div className="p-8 space-y-8">
                <Tabs defaultValue="periods" className="w-full">
                    <TabsList className="bg-surface-card border border-line-default mb-6">
                        <TabsTrigger value="periods" className="data-[state=active]:bg-brand-red data-[state=active]:text-white gap-2">
                            <Calendar className="w-4 h-4" /> Period Comparison
                        </TabsTrigger>
                        <TabsTrigger value="trends" className="data-[state=active]:bg-brand-red data-[state=active]:text-white gap-2">
                            <LineChartIcon className="w-4 h-4" /> Trend Analysis
                        </TabsTrigger>
                        <TabsTrigger value="profiles" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white gap-2">
                            <Linkedin className="w-4 h-4" /> Profile Comparison
                        </TabsTrigger>
                    </TabsList>

                    {/* Period Comparison Tab */}
                    <TabsContent value="periods" className="space-y-6">
                        {/* Selection Controls */}
                        <div className="card-base p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                                <div className="space-y-2">
                                    <label className="text-sm text-content-secondary">Comparison Type</label>
                                    <Select value={comparisonType} onValueChange={setComparisonType}>
                                        <SelectTrigger className="input-base">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface-card border-line-default">
                                            <SelectItem value="wow">Week-on-Week</SelectItem>
                                            <SelectItem value="mom">Month-on-Month</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm text-content-secondary">Previous Period</label>
                                    <Select value={period2Id} onValueChange={setPeriod2Id}>
                                        <SelectTrigger className="input-base">
                                            <SelectValue placeholder="Select period" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface-card border-line-default max-h-60">
                                            {reports.map((report) => (
                                                <SelectItem key={report.id} value={report.id}>
                                                    {formatDateRange(report.period_start, report.period_end)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm text-content-secondary">Current Period</label>
                                    <Select value={period1Id} onValueChange={setPeriod1Id}>
                                        <SelectTrigger className="input-base">
                                            <SelectValue placeholder="Select period" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-surface-card border-line-default max-h-60">
                                            {reports.map((report) => (
                                                <SelectItem key={report.id} value={report.id}>
                                                    {formatDateRange(report.period_start, report.period_end)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button onClick={handleCompare} disabled={comparing} className="btn-primary">
                                    {comparing ? (
                                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                    ) : null}
                                    Compare
                                </Button>
                            </div>
                        </div>

                        {/* Comparison Results */}
                        {comparisonData && (
                            <div className="space-y-6">
                                {/* Period Labels */}
                                <div className="flex items-center justify-center gap-8 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-content-muted" />
                                        <span className="text-content-secondary">Previous:</span>
                                        <Badge className="badge badge-outline">
                                            {formatDateRange(comparisonData.period2.start, comparisonData.period2.end)}
                                        </Badge>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-brand-red" />
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-content-muted" />
                                        <span className="text-content-secondary">Current:</span>
                                        <Badge className="badge badge-info">
                                            {formatDateRange(comparisonData.period1.start, comparisonData.period1.end)}
                                        </Badge>
                                    </div>
                                </div>

                                {/* Metrics Comparison */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Overall Metrics */}
                                    <div className="card-base p-6">
                                        <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
                                            <BarChart3 className="w-5 h-5 text-brand-red" />
                                            OVERALL METRICS
                                        </h3>
                                        <div className="flex items-center justify-between text-xs text-content-muted mb-2 px-2">
                                            <span>Metric</span>
                                            <div className="flex items-center gap-6">
                                                <span className="w-20 text-right">Previous</span>
                                                <span className="w-4"></span>
                                                <span className="w-20 text-right">Current</span>
                                                <span className="w-24 text-right">Change</span>
                                            </div>
                                        </div>
                                        {comparisonData.metrics && Object.entries(comparisonData.metrics).map(([key, data]) => (
                                            <MetricComparisonRow 
                                                key={key}
                                                label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                current={data.current}
                                                previous={data.previous}
                                            />
                                        ))}
                                    </div>

                                    {/* LinkedIn Metrics */}
                                    <div className="card-base p-6">
                                        <h3 className="text-lg font-heading font-bold text-white mb-4 flex items-center gap-2">
                                            <Linkedin className="w-5 h-5 text-channel-linkedin" />
                                            LINKEDIN METRICS
                                        </h3>
                                        <div className="flex items-center justify-between text-xs text-content-muted mb-2 px-2">
                                            <span>Metric</span>
                                            <div className="flex items-center gap-6">
                                                <span className="w-20 text-right">Previous</span>
                                                <span className="w-4"></span>
                                                <span className="w-20 text-right">Current</span>
                                                <span className="w-24 text-right">Change</span>
                                            </div>
                                        </div>
                                        {comparisonData.linkedin && Object.entries(comparisonData.linkedin).map(([key, data]) => (
                                            <MetricComparisonRow 
                                                key={key}
                                                label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                current={data.current}
                                                previous={data.previous}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {!comparisonData && (
                            <div className="card-base p-12 text-center">
                                <BarChart3 className="w-16 h-16 text-content-muted mx-auto mb-4" />
                                <h3 className="text-lg font-heading font-bold text-white mb-2">Compare Report Periods</h3>
                                <p className="text-content-secondary">
                                    Select two periods above and click Compare to see performance changes
                                </p>
                            </div>
                        )}
                    </TabsContent>

                    {/* Trend Analysis Tab */}
                    <TabsContent value="trends" className="space-y-6">
                        <TrendAnalysisSection reports={reports} />
                    </TabsContent>

                    {/* Profile Comparison Tab */}
                    <TabsContent value="profiles" className="space-y-6">
                        <div className="card-base p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-heading font-bold text-white flex items-center gap-2">
                                        <Users className="w-5 h-5 text-channel-linkedin" />
                                        Select LinkedIn Profiles to Compare
                                    </h3>
                                    <p className="text-sm text-content-muted mt-1">
                                        Select 2-5 profiles to compare their performance metrics
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Badge className="badge badge-info">{selectedProfiles.length}/5 selected</Badge>
                                    <Button 
                                        onClick={handleCompareProfiles} 
                                        disabled={comparingProfiles || selectedProfiles.length < 2}
                                        className="btn-primary"
                                    >
                                        {comparingProfiles ? (
                                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                        ) : null}
                                        Compare Profiles
                                    </Button>
                                </div>
                            </div>

                            {allProfiles.length === 0 ? (
                                <div className="text-center py-8 border border-dashed border-line-default rounded-lg">
                                    <Linkedin className="w-12 h-12 text-content-muted mx-auto mb-3" />
                                    <p className="text-content-secondary">No LinkedIn profiles found. Add profile data from Admin Reports.</p>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-3">
                                    {allProfiles.map((profile) => (
                                        <div
                                            key={profile.profile_name}
                                            onClick={() => toggleProfileSelection(profile.profile_name)}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                                                selectedProfiles.includes(profile.profile_name)
                                                    ? 'bg-channel-linkedin/20 border-channel-linkedin text-white'
                                                    : 'bg-surface-app border-line-default text-content-secondary hover:border-channel-linkedin/50'
                                            }`}
                                        >
                                            <Checkbox 
                                                checked={selectedProfiles.includes(profile.profile_name)}
                                                className="data-[state=checked]:bg-channel-linkedin data-[state=checked]:border-channel-linkedin"
                                            />
                                            <div>
                                                <span className="font-medium">{profile.profile_name}</span>
                                                <span className="text-xs text-content-muted ml-2">
                                                    ({profile.mqls} MQLs, {profile.qualified_leads} SQLs)
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Profile Comparison Results */}
                        {profileComparisonData.length > 0 && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-heading font-bold text-white flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-channel-linkedin" />
                                    Profile Comparison Results
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                                    {profileComparisonData.map((profile, index) => (
                                        <ProfileComparisonCard key={index} profile={profile} />
                                    ))}
                                </div>

                                {/* Summary Table */}
                                <div className="card-base overflow-hidden">
                                    <table className="w-full data-table">
                                        <thead>
                                            <tr className="bg-surface-app">
                                                <th className="text-left p-4 text-content-muted">Profile</th>
                                                <th className="text-right p-4 text-content-muted">Reachouts</th>
                                                <th className="text-right p-4 text-content-muted">Accepted</th>
                                                <th className="text-right p-4 text-content-muted">Responses</th>
                                                <th className="text-right p-4 text-content-muted">Accept %</th>
                                                <th className="text-right p-4 text-content-muted">Response %</th>
                                                <th className="text-right p-4 text-brand-red">MQLs</th>
                                                <th className="text-right p-4 text-status-success">SQLs</th>
                                                <th className="text-right p-4 text-brand-red">MQL %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profileComparisonData.map((profile, index) => {
                                                const acceptRate = calculatePercentage(profile.connection_accepted, profile.reachouts_sent);
                                                const responseRate = calculatePercentage(profile.responses, profile.reachouts_sent);
                                                const mqlRate = calculatePercentage(profile.mqls, profile.reachouts_sent);
                                                return (
                                                    <tr key={index} className="border-t border-line-default">
                                                        <td className="p-4">
                                                            <div className="flex items-center gap-2">
                                                                <Linkedin className="w-4 h-4 text-channel-linkedin" />
                                                                <span className="text-white font-medium">{profile.profile_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-right text-white">{formatNumber(profile.reachouts_sent)}</td>
                                                        <td className="p-4 text-right text-channel-linkedin">{formatNumber(profile.connection_accepted)}</td>
                                                        <td className="p-4 text-right text-white">{formatNumber(profile.responses)}</td>
                                                        <td className="p-4 text-right text-channel-linkedin font-bold">{acceptRate}%</td>
                                                        <td className="p-4 text-right text-white font-bold">{responseRate}%</td>
                                                        <td className="p-4 text-right text-brand-red font-bold">{profile.mqls}</td>
                                                        <td className="p-4 text-right text-status-success font-bold">{profile.qualified_leads}</td>
                                                        <td className="p-4 text-right text-brand-red font-bold">{mqlRate}%</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

const customTooltipStyle = {
    backgroundColor: '#191C24',
    border: '1px solid #2D2D3D',
    borderRadius: '8px',
    padding: '8px 12px',
};

function TrendMetricChart({ data, dataKey, title, color, type = 'line' }) {
    return (
        <div className="card-base p-5" data-testid={`trend-chart-${dataKey}`}>
            <h4 className="text-sm font-heading font-bold text-white mb-4">{title}</h4>
            <ResponsiveContainer width="100%" height={220}>
                {type === 'bar' ? (
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
                        <XAxis dataKey="label" tick={{ fill: '#A1A1AA', fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <Tooltip contentStyle={customTooltipStyle} labelStyle={{ color: '#fff' }} />
                        <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
                    </BarChart>
                ) : (
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
                        <XAxis dataKey="label" tick={{ fill: '#A1A1AA', fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <Tooltip contentStyle={customTooltipStyle} labelStyle={{ color: '#fff' }} />
                        <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2.5} dot={{ r: 4, fill: color }} activeDot={{ r: 6 }} />
                    </LineChart>
                )}
            </ResponsiveContainer>
        </div>
    );
}

function TrendAnalysisSection({ reports }) {
    const [viewType, setViewType] = useState('weekly');

    // Filter reports by type
    const weeklyReports = reports.filter(r => r.report_type === 'weekly').slice().reverse();
    const monthlyReports = reports.filter(r => r.report_type === 'monthly').slice().reverse();
    const activeReports = viewType === 'weekly' ? weeklyReports : monthlyReports;

    // Build chart data: combined LinkedIn + Email per the user's mapping
    const chartData = activeReports.map((r) => {
        const li = r.linkedin || {};
        const em = r.email || {};
        const start = r.period_start ? new Date(r.period_start) : null;
        const label = start
            ? `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
            : 'N/A';
        return {
            label,
            reachouts: (li.reachouts_sent || 0) + (em.emails_sent || 0),
            connects: li.connection_accepted || 0,
            responses: (li.responses || 0) + (em.replies || 0),
            positive_responses: (li.positive_responses || 0) + (em.positive_replies || 0),
            mqls: (li.mqls || 0) + (em.mqls || 0),
            sqls: (li.qualified_leads || 0) + (em.qualified_leads || 0),
        };
    });

    // Cumulative data
    let cumReachouts = 0, cumConnects = 0, cumResponses = 0, cumPositive = 0, cumMqls = 0, cumSqls = 0;
    const cumulativeData = chartData.map((d) => {
        cumReachouts += d.reachouts;
        cumConnects += d.connects;
        cumResponses += d.responses;
        cumPositive += d.positive_responses;
        cumMqls += d.mqls;
        cumSqls += d.sqls;
        return {
            label: d.label,
            reachouts: cumReachouts,
            connects: cumConnects,
            responses: cumResponses,
            positive_responses: cumPositive,
            mqls: cumMqls,
            sqls: cumSqls,
        };
    });

    const totalReachouts = cumReachouts;
    const totalConnects = cumConnects;
    const totalResponses = cumResponses;
    const totalPositive = cumPositive;
    const totalMqls = cumMqls;
    const totalSqls = cumSqls;

    return (
        <div className="space-y-6" data-testid="trend-analysis-section">
            {/* Controls */}
            <div className="card-base p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-heading font-bold text-white flex items-center gap-2">
                            <LineChartIcon className="w-5 h-5 text-brand-red" />
                            Trend Analysis — Beginning to Date
                        </h3>
                        <p className="text-sm text-content-muted mt-1">
                            Cumulative and per-period performance across all reports
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant={viewType === 'weekly' ? 'default' : 'ghost'}
                            className={viewType === 'weekly' ? 'btn-primary' : 'btn-ghost'}
                            size="sm"
                            onClick={() => setViewType('weekly')}
                            data-testid="trend-weekly-btn"
                        >
                            Weekly
                        </Button>
                        <Button
                            variant={viewType === 'monthly' ? 'default' : 'ghost'}
                            className={viewType === 'monthly' ? 'btn-primary' : 'btn-ghost'}
                            size="sm"
                            onClick={() => setViewType('monthly')}
                            data-testid="trend-monthly-btn"
                        >
                            Monthly
                        </Button>
                    </div>
                </div>
            </div>

            {activeReports.length === 0 ? (
                <div className="card-base p-12 text-center">
                    <LineChartIcon className="w-16 h-16 text-content-muted mx-auto mb-4" />
                    <h3 className="text-lg font-heading font-bold text-white mb-2">No {viewType} reports found</h3>
                    <p className="text-content-secondary">Switch to {viewType === 'weekly' ? 'monthly' : 'weekly'} or ask your admin to create reports.</p>
                </div>
            ) : (
                <>
                    {/* Cumulative Totals */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                        {[
                            { label: 'Total Reachouts', value: totalReachouts, color: 'text-white' },
                            { label: 'Total Connects', value: totalConnects, color: 'text-channel-linkedin' },
                            { label: 'Total Responses', value: totalResponses, color: 'text-white' },
                            { label: 'Positive Responses', value: totalPositive, color: 'text-status-success' },
                            { label: 'Total MQLs', value: totalMqls, color: 'text-brand-red' },
                            { label: 'Total SQLs', value: totalSqls, color: 'text-status-success' },
                        ].map(({ label, value, color }) => (
                            <div key={label} className="card-base p-4 text-center">
                                <p className={`text-2xl font-heading font-bold ${color}`}>{formatNumber(value)}</p>
                                <p className="text-xs text-content-muted mt-1">{label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Per-Period Charts */}
                    <h4 className="text-sm font-heading font-bold text-content-muted uppercase tracking-wider">Per-Period Breakdown</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TrendMetricChart data={chartData} dataKey="reachouts" title="REACHOUTS" color="#FFFFFF" type="bar" />
                        <TrendMetricChart data={chartData} dataKey="connects" title="CONNECTS" color="#0077B5" type="bar" />
                        <TrendMetricChart data={chartData} dataKey="responses" title="RESPONSES" color="#A1A1AA" type="bar" />
                        <TrendMetricChart data={chartData} dataKey="positive_responses" title="POSITIVE RESPONSES" color="#10B981" type="bar" />
                        <TrendMetricChart data={chartData} dataKey="mqls" title="MQLs" color="#DC2626" type="bar" />
                        <TrendMetricChart data={chartData} dataKey="sqls" title="SQLs" color="#10B981" type="bar" />
                    </div>

                    {/* Cumulative Charts */}
                    <h4 className="text-sm font-heading font-bold text-content-muted uppercase tracking-wider">Cumulative Growth</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <TrendMetricChart data={cumulativeData} dataKey="reachouts" title="CUMULATIVE REACHOUTS" color="#FFFFFF" />
                        <TrendMetricChart data={cumulativeData} dataKey="connects" title="CUMULATIVE CONNECTS" color="#0077B5" />
                        <TrendMetricChart data={cumulativeData} dataKey="positive_responses" title="CUMULATIVE POSITIVE RESPONSES" color="#10B981" />
                        <TrendMetricChart data={cumulativeData} dataKey="mqls" title="CUMULATIVE MQLs" color="#DC2626" />
                        <TrendMetricChart data={cumulativeData} dataKey="sqls" title="CUMULATIVE SQLs" color="#10B981" />
                        <TrendMetricChart data={cumulativeData} dataKey="responses" title="CUMULATIVE RESPONSES" color="#A1A1AA" />
                    </div>
                </>
            )}
        </div>
    );
}
